import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components';
import { useUiStore } from '@/stores/ui';
import { t } from '@/i18n';

/** How often a long-lived tab asks whether a new version has shipped. */
export const UPDATE_CHECK_MS = 60 * 60 * 1000;

/**
 * Registers the service worker and offers updates (plan doc 17).
 *
 * `registerType: 'prompt'` means a new worker waits until asked. This is the
 * asking: a bar above the tab bar with "Update" and "Later". Later is honest —
 * the new version applies on the next launch regardless — so nothing is lost
 * by declining mid-lesson.
 *
 * "Ready to work offline" is said once, as a toast, the first time the shell
 * finishes precaching. It is the only time the service worker speaks unasked.
 */
export function UpdateToast() {
  const toast = useUiStore((s) => s.toast);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration === undefined) return;
      // A tab left open for days would otherwise never hear about a release.
      setInterval(() => void registration.update(), UPDATE_CHECK_MS);
    },
    onRegisterError(error: unknown) {
      console.warn('[pwa] service worker registration failed', error);
    },
  });

  useEffect(() => {
    if (!offlineReady) return;
    toast(t('pwa.offlineReady'), { tone: 'success' });
    setOfflineReady(false);
  }, [offlineReady, setOfflineReady, toast]);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-3 bottom-[calc(var(--tabbar-h)+env(safe-area-inset-bottom,0px)+12px)] z-40 mx-auto flex max-w-[var(--content-max)] items-center gap-3 rounded-xl border-2 border-line bg-surface px-4 py-3 shadow-lg lg:left-60"
    >
      <RefreshCw size={20} aria-hidden className="shrink-0 text-brand" />
      <p className="flex-1 text-sm font-semibold">{t('pwa.updateReady')}</p>
      <Button size="sm" variant="neutral" onClick={() => setNeedRefresh(false)}>
        {t('pwa.updateLater')}
      </Button>
      <Button size="sm" onClick={() => void updateServiceWorker(true)}>
        {t('pwa.updateNow')}
      </Button>
    </div>
  );
}
